import {
  Container,
  Heading,
  Text,
  Stack,
  Box,
  Flex,
  Icon,
  useColorModeValue,
  HStack,
  IconButton,
  keyframes,
} from "@chakra-ui/react";
import { useReducedMotion } from "framer-motion";
import { useState, useEffect, useMemo } from "react";
import { FiClock, FiBell, FiChevronLeft, FiChevronRight } from "react-icons/fi";

// Design token constant — prevents duplicate-string lint error
const TOKEN_BORDER_DARK = "neutral.800";

interface TestimonialProps {
  quote: string;
  author: string;
}

const fadeIn = keyframes`
  from { opacity: 0; transform: translateX(10px); }
  to { opacity: 1; transform: translateX(0); }
`;

const testimonials: TestimonialProps[] = [
  {
    quote:
      "Reasonably priced service with location-specific, real-time updates that integrated seamlessly with my Garmin device. I'll definitely be using it for all my future adventures.",
    author: "Blaine B.",
  },
  {
    quote:
      "I started using Adiona on this trip after the recent devastating floods in Texas, where so many lives were tragically lost simply because people weren't aware of the extreme weather conditions approaching.",
    author: "Ken G.",
  },
  {
    quote:
      "I'm genuinely impressed by the exceptional customer service and prompt response I received. It's refreshing to work with a company that values its customers.",
    author: "Mike R.",
  },
  {
    quote:
      "I spent the weekend in the region doing a few-hundred-mile driving loop and hiking. With my InReach Mini set to tracking mode, it performed flawlessly throughout the entire trip. The system absolutely delivers on its promises.",
    author: "Jordan P.",
  },
  {
    quote:
      "The system is really proving its worth here in Western Washington today. Multiple flood alerts and warnings have been sent out due to the torrential rainfall we've experienced over the past few hours. It's exactly what you want during severe weather events like this.",
    author: "Steve B.",
  },
  {
    quote:
      "I've had to evacuate from the backcountry due to two separate wildfires, and the lack of real-time information was both a major source of stress and a serious safety risk. Your service is invaluable in those situations—and will be essential for all my future backcountry adventures.",
    author: "Evan W.",
  },
  {
    quote:
      "I hadn't fully considered how valuable winter storm warnings could be until now. Once or twice a year, my friends and I go backcountry camping in areas with no cell service. While I've been relying on weather forecasts from my InReach, having proactive alerts is a significant safety upgrade.",
    author: "Mark G.",
  },
  {
    quote:
      "I'm grateful for the assistance you provided before my hike, and I was especially impressed by how quickly you reached out with critical information about the flooding in Havasupai. That kind of proactive communication can be the difference between a safe trip and a dangerous situation.",
    author: "Melissa P.",
  },
  {
    quote:
      "We had a frightening run-in with a severe thunderstorm while backpacking this weekend, and it really opened my eyes to how vulnerable we were out there. That experience made me realize I need reliable weather alerts to give me peace of mind during future backcountry adventures.",
    author: "Cynthia R.",
  },
];

const TestimonialCard = ({ quote, author }: TestimonialProps) => {
  const bg = useColorModeValue("white", "neutral.900");
  const borderColor = useColorModeValue("neutral.100", TOKEN_BORDER_DARK);
  const quoteColor = useColorModeValue(
    "text.light-secondary",
    "text.dark-muted"
  );
  const authorColor = useColorModeValue(
    "text.light-primary",
    "text.dark-primary"
  );

  return (
    <Box
      p={7}
      bg={bg}
      borderRadius="lg"
      border="1px solid"
      borderColor={borderColor}
    >
      <Stack spacing={5}>
        <Text
          color={quoteColor}
          fontSize="md"
          lineHeight="1.8"
          fontStyle="italic"
        >
          &ldquo;{quote}&rdquo;
        </Text>
        <Text fontWeight={700} color={authorColor} fontSize="sm">
          — {author}
        </Text>
      </Stack>
    </Box>
  );
};

export default function Feature3() {
  const shouldReduceMotion = useReducedMotion();
  const [currentPage, setCurrentPage] = useState(0);
  const [mobileCurrentPage, setMobileCurrentPage] = useState(0);
  const [isPaused, setIsPaused] = useState(false);

  const headingColor = useColorModeValue(
    "text.light-primary",
    "text.dark-primary"
  );
  const statLabelColor = useColorModeValue(
    "text.light-secondary",
    "text.dark-muted"
  );
  const navBg = useColorModeValue("white", "neutral.900");
  const navHoverBg = useColorModeValue("neutral.50", TOKEN_BORDER_DARK);
  const dotInactive = useColorModeValue("neutral.100", TOKEN_BORDER_DARK);

  const desktopPerPage = 3;
  const totalDesktopPages = Math.ceil(testimonials.length / desktopPerPage);
  const totalMobilePages = testimonials.length;

  useEffect(() => {
    if (isPaused || shouldReduceMotion) return undefined;

    const interval = setInterval(() => {
      setCurrentPage((prev) => (prev + 1) % totalDesktopPages);
      setMobileCurrentPage((prev) => (prev + 1) % totalMobilePages);
    }, 5000);

    return () => clearInterval(interval);
  }, [isPaused, shouldReduceMotion, totalDesktopPages, totalMobilePages]);

  const desktopTestimonials = useMemo(() => {
    const startIndex = currentPage * desktopPerPage;
    return testimonials.slice(startIndex, startIndex + desktopPerPage);
  }, [currentPage, desktopPerPage]);

  return (
    <Container maxW="6xl" py={{ base: 20, md: 28 }}>
      <Stack spacing={{ base: 14, md: 16 }}>
        {/* Stats */}
        <Stack spacing={4} textAlign="center">
          <Heading
            color={headingColor}
            fontWeight={800}
            fontSize={{ base: "3xl", md: "4xl", lg: "5xl" }}
            letterSpacing="-0.02em"
          >
            Protection You Can{" "}
            <Text as="span" color="brand.500">
              Count On
            </Text>
          </Heading>
          <Flex
            justify="center"
            gap={{ base: 8, md: 12 }}
            flexWrap="wrap"
            pt={1}
          >
            <HStack spacing={2}>
              <Icon as={FiClock} color="brand.500" boxSize={5} />
              <Text fontWeight={700} color="brand.500" fontSize="lg">
                24/7
              </Text>
              <Text color={statLabelColor}>Monitoring</Text>
            </HStack>
            <HStack spacing={2}>
              <Icon as={FiBell} color="brand.500" boxSize={5} />
              <Text fontWeight={700} color="brand.500" fontSize="lg">
                20K+
              </Text>
              <Text color={statLabelColor}>Alerts Delivered</Text>
            </HStack>
          </Flex>
        </Stack>

        {/* Testimonials */}
        <Stack spacing={6}>
          {/* Desktop: 3 cards */}
          <Box
            display={{ base: "none", md: "block" }}
            position="relative"
            onMouseEnter={() => setIsPaused(true)}
            onMouseLeave={() => setIsPaused(false)}
            onFocus={() => setIsPaused(true)}
            onBlur={() => setIsPaused(false)}
          >
            <Flex
              gap={5}
              key={currentPage}
              animation={
                shouldReduceMotion ? undefined : `${fadeIn} 0.35s ease-out`
              }
            >
              {desktopTestimonials.map((testimonial) => (
                <Box key={testimonial.quote.slice(0, 50)} flex={1}>
                  <TestimonialCard
                    quote={testimonial.quote}
                    author={testimonial.author}
                  />
                </Box>
              ))}
            </Flex>

            <IconButton
              aria-label="Previous testimonials"
              icon={<Icon as={FiChevronLeft} boxSize={5} />}
              position="absolute"
              left={-5}
              top="50%"
              transform="translateY(-50%)"
              bg={navBg}
              border="1px solid"
              borderColor={dotInactive}
              borderRadius="full"
              size="sm"
              onClick={() =>
                setCurrentPage(
                  (prev) => (prev - 1 + totalDesktopPages) % totalDesktopPages
                )
              }
              _hover={{ bg: navHoverBg }}
            />
            <IconButton
              aria-label="Next testimonials"
              icon={<Icon as={FiChevronRight} boxSize={5} />}
              position="absolute"
              right={-5}
              top="50%"
              transform="translateY(-50%)"
              bg={navBg}
              border="1px solid"
              borderColor={dotInactive}
              borderRadius="full"
              size="sm"
              onClick={() =>
                setCurrentPage((prev) => (prev + 1) % totalDesktopPages)
              }
              _hover={{ bg: navHoverBg }}
            />
          </Box>

          {/* Desktop dots */}
          <HStack
            display={{ base: "none", md: "flex" }}
            justify="center"
            spacing={2}
          >
            {Array.from({ length: totalDesktopPages }).map((_, idx) => (
              <Box
                key={`page-dot-${idx.toString()}`}
                as="button"
                w={6}
                h={6}
                borderRadius="full"
                display="flex"
                alignItems="center"
                justifyContent="center"
                cursor="pointer"
                onClick={() => setCurrentPage(idx)}
                aria-label={`Go to page ${idx + 1}`}
                aria-pressed={idx === currentPage}
                bg="transparent"
              >
                <Box
                  w={2}
                  h={2}
                  borderRadius="full"
                  bg={idx === currentPage ? "brand.500" : dotInactive}
                  transition="background-color 0.2s"
                />
              </Box>
            ))}
          </HStack>

          {/* Mobile: single card */}
          <Box
            display={{ base: "block", md: "none" }}
            position="relative"
            onMouseEnter={() => setIsPaused(true)}
            onMouseLeave={() => setIsPaused(false)}
            onFocus={() => setIsPaused(true)}
            onBlur={() => setIsPaused(false)}
          >
            <Box
              key={`mobile-${mobileCurrentPage}`}
              animation={
                shouldReduceMotion ? undefined : `${fadeIn} 0.35s ease-out`
              }
            >
              <TestimonialCard
                quote={testimonials[mobileCurrentPage].quote}
                author={testimonials[mobileCurrentPage].author}
              />
            </Box>

            <IconButton
              aria-label="Previous testimonial"
              icon={<Icon as={FiChevronLeft} boxSize={5} />}
              position="absolute"
              left={-2}
              top="50%"
              transform="translateY(-50%)"
              bg={navBg}
              border="1px solid"
              borderColor={dotInactive}
              borderRadius="full"
              size="sm"
              onClick={() =>
                setMobileCurrentPage(
                  (prev) => (prev - 1 + totalMobilePages) % totalMobilePages
                )
              }
              _hover={{ bg: navHoverBg }}
            />
            <IconButton
              aria-label="Next testimonial"
              icon={<Icon as={FiChevronRight} boxSize={5} />}
              position="absolute"
              right={-2}
              top="50%"
              transform="translateY(-50%)"
              bg={navBg}
              border="1px solid"
              borderColor={dotInactive}
              borderRadius="full"
              size="sm"
              onClick={() =>
                setMobileCurrentPage((prev) => (prev + 1) % totalMobilePages)
              }
              _hover={{ bg: navHoverBg }}
            />
          </Box>

          {/* Mobile dots */}
          <HStack
            display={{ base: "flex", md: "none" }}
            justify="center"
            spacing={2}
          >
            {Array.from({ length: totalMobilePages }).map((_, idx) => (
              <Box
                key={`mobile-dot-${idx.toString()}`}
                as="button"
                w={6}
                h={6}
                borderRadius="full"
                display="flex"
                alignItems="center"
                justifyContent="center"
                cursor="pointer"
                onClick={() => setMobileCurrentPage(idx)}
                aria-label={`Go to testimonial ${idx + 1}`}
                aria-pressed={idx === mobileCurrentPage}
                bg="transparent"
              >
                <Box
                  w={idx === mobileCurrentPage ? 2.5 : 1.5}
                  h={idx === mobileCurrentPage ? 2.5 : 1.5}
                  borderRadius="full"
                  bg={idx === mobileCurrentPage ? "brand.500" : dotInactive}
                  transition="all 0.2s"
                />
              </Box>
            ))}
          </HStack>
        </Stack>
      </Stack>
    </Container>
  );
}
